import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Bell, Shield, Copy, Check, Settings, Send, Mail, MessageSquare, Database, Upload, X, Pencil, Link2, RefreshCw, FileSpreadsheet, Users } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

// ---------------- Alerts ----------------
export function AlertsDialog({ catalog, canEdit }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ metric: "revenue", operator: "gt", threshold: "", severity: "warning" });

  const load = () => api.get("/alerts").then((r) => setRows(r.data));
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (form.threshold === "") return toast.error("Enter a threshold value");
    try {
      await api.post("/alerts", { ...form, threshold: parseFloat(form.threshold) });
      toast.success("Alert rule created");
      setForm({ ...form, threshold: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create alert");
    }
  };

  const remove = async (id) => { await api.delete(`/alerts/${id}`); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-alerts-button"><Bell className="h-4 w-4 mr-1.5" />Alerts</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="alerts-dialog">
        <DialogHeader><DialogTitle className="font-heading">Threshold Alerts</DialogTitle><DialogDescription>Configure KPI thresholds that trigger notifications.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">No alert rules yet</p>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`alert-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <Badge variant={r.severity === "danger" ? "destructive" : "secondary"} className="text-[10px] uppercase">{r.severity}</Badge>
                <span className="text-sm font-mono">{catalog[r.metric]?.label || r.metric} {r.operator === "gt" ? ">" : "<"} {r.threshold}</span>
                {r.breached && <span className="h-2 w-2 rounded-full bg-destructive animate-pulse" title="breached" />}
              </div>
              {canEdit && <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-alert-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Metric</Label>
                <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                  <SelectTrigger data-testid="alert-metric-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(catalog).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Condition</Label>
                <Select value={form.operator} onValueChange={(v) => setForm({ ...form, operator: v })}>
                  <SelectTrigger data-testid="alert-operator-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gt">Greater than</SelectItem>
                    <SelectItem value="lt">Less than</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Threshold</Label>
                <Input type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} data-testid="alert-threshold-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="alert-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="danger">Danger</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={create} className="w-full" size="sm" data-testid="create-alert-button"><Plus className="h-4 w-4 mr-1" />Add alert rule</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Add Widget ----------------
const WIDGET_TYPES = [
  { type: "kpi", label: "KPI Card", span: 1 },
  { type: "line", label: "Line Chart", span: 2 },
  { type: "area", label: "Area Chart", span: 2 },
  { type: "bar", label: "Bar (by Region)", span: 1, by: "region" },
  { type: "gauge", label: "Gauge", span: 1 },
];

export function AddWidgetDialog({ catalog, onAdd, allowBar = true }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("kpi");
  const metricKeys = Object.keys(catalog);
  const [metric, setMetric] = useState(metricKeys[0] || "");
  useEffect(() => { setMetric(metricKeys[0] || ""); /* eslint-disable-next-line */ }, [JSON.stringify(metricKeys)]);
  const types = WIDGET_TYPES.filter((w) => allowBar || w.type !== "bar");

  const add = () => {
    if (!metric) return toast.error("This data source has no metrics yet");
    const def = types.find((w) => w.type === type) || WIDGET_TYPES[0];
    const widget = { id: "w" + Date.now(), type, metric, span: def.span, title: catalog[metric]?.label };
    if (def.by) widget.by = def.by;
    onAdd(widget);
    setOpen(false);
    toast.success("Widget added");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-widget-button"><Plus className="h-4 w-4 mr-1" />Add Widget</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-widget-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add Widget</DialogTitle><DialogDescription>Add a new metric widget to your dashboard.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Widget type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="widget-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((w) => <SelectItem key={w.type} value={w.type}>{w.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Metric</Label>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger data-testid="widget-metric-select"><SelectValue placeholder="Select metric" /></SelectTrigger>
              <SelectContent>{Object.values(catalog).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={add} data-testid="confirm-add-widget-button">Add to dashboard</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Share ----------------
export function ShareDialog() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", role: "viewer" });
  const [copied, setCopied] = useState("");

  const load = () => api.get("/shares").then((r) => setRows(r.data));
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (!form.name) return toast.error("Give the link a name");
    await api.post("/shares", form);
    setForm({ name: "", role: "viewer" });
    load();
    toast.success("Share link created");
  };
  const remove = async (id) => { await api.delete(`/shares/${id}`); load(); };
  const copy = (token, id) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(id);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-share-button"><Copy className="h-4 w-4 mr-1.5" />Share</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-heading">Role-based Sharing</DialogTitle><DialogDescription>Create and manage read-only share links by role.</DialogDescription></DialogHeader>
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Link name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Q2 Exec Review" data-testid="share-name-input" />
          </div>
          <div className="space-y-1 w-32">
            <Label className="text-xs">Access</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="share-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={create} data-testid="create-share-button"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`share-row-${r.id}`}>
              <div>
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground font-mono">/shared/{r.token} · {r.role}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => copy(r.token, r.id)} className="h-7 w-7 flex items-center justify-center rounded border border-border hover:bg-secondary transition-colors" data-testid={`copy-share-${r.id}`}>
                  {copied === r.id ? <Check className="h-3.5 w-3.5 text-[hsl(142_71%_45%)]" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => remove(r.id)} className="h-7 w-7 flex items-center justify-center rounded border border-border hover:text-destructive transition-colors" data-testid={`delete-share-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Admin Users ----------------
export function UsersDialog() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { if (open) load(); }, [open]);

  const setRole = async (id, role) => { await api.patch(`/users/${id}/role`, { role }); load(); toast.success("Role updated"); };
  const remove = async (id) => { try { await api.delete(`/users/${id}`); load(); toast.success("User removed"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-users-button"><Shield className="h-4 w-4 mr-1.5" />Users</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="users-dialog">
        <DialogHeader><DialogTitle className="font-heading">User Management</DialogTitle><DialogDescription>Manage users and their roles.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {rows.map((u) => (
            <div key={u.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`user-row-${u.email}`}>
              <div>
                <div className="text-sm font-medium">{u.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                  <SelectTrigger className="h-8 w-28" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => remove(u.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------------- Settings (alerts channels + scheduled reports) ----------------
export function SettingsDialog() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [weekly, setWeekly] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/settings").then((r) => setS(r.data));
      api.get("/me/email-prefs").then((r) => setWeekly(!!r.data.weekly_summary)).catch(() => {});
    }
  }, [open]);

  const toggleWeekly = async (v) => {
    setWeekly(v);
    try {
      const r = await api.put("/me/email-prefs", { weekly_summary: v });
      if (v && !r.data.email_configured) toast.warning("Weekly summary on — delivery is MOCKED until a Resend key is added");
      else toast.success(v ? "Weekly summary enabled" : "Weekly summary disabled");
    } catch (e) {
      setWeekly(!v);
      toast.error(e.response?.data?.detail || "Failed to update preference");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { integrations, _id, ...payload } = s;
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    try {
      const r = await api.post("/reports/send-now");
      if (r.data.mocked) toast.warning(`Report generated for ${r.data.recipient} — email is MOCKED (add RESEND_API_KEY to send for real)`);
      else toast.success(`Report emailed to ${r.data.recipient}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send report");
    }
  };

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-settings-button"><Settings className="h-4 w-4 mr-1.5" />Settings</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="settings-dialog">
        <DialogHeader><DialogTitle className="font-heading">Delivery Settings</DialogTitle><DialogDescription>Configure report email and alert delivery preferences.</DialogDescription></DialogHeader>
        {!s ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p> : (
          <div className="space-y-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Alert delivery on breach</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm"><MessageSquare className="h-4 w-4 text-muted-foreground" />Slack
                    <Badge variant={s.integrations?.slack_configured ? "secondary" : "outline"} className="text-[10px]">{s.integrations?.slack_configured ? "connected" : "not configured"}</Badge>
                  </div>
                  <Switch checked={s.alert_slack_enabled} onCheckedChange={(v) => set("alert_slack_enabled", v)} data-testid="toggle-slack-switch" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-muted-foreground" />Email
                    <Badge variant={s.integrations?.email_configured ? "secondary" : "outline"} className="text-[10px]">{s.integrations?.email_configured ? "connected" : "not configured"}</Badge>
                  </div>
                  <Switch checked={s.alert_email_enabled} onCheckedChange={(v) => set("alert_email_enabled", v)} data-testid="toggle-email-switch" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alert email recipient</Label>
                  <Input type="email" value={s.alert_recipient} onChange={(e) => set("alert_recipient", e.target.value)} placeholder="ops@company.com" data-testid="alert-recipient-input" />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Scheduled reports</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Enable scheduled email reports</span>
                  <Switch checked={s.report_enabled} onCheckedChange={(v) => set("report_enabled", v)} data-testid="toggle-report-switch" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Frequency</Label>
                    <Select value={s.report_frequency} onValueChange={(v) => set("report_frequency", v)}>
                      <SelectTrigger data-testid="report-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily (08:00 UTC)</SelectItem>
                        <SelectItem value="weekly">Weekly (Mon 08:00 UTC)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Range</Label>
                    <Select value={s.report_range} onValueChange={(v) => set("report_range", v)}>
                      <SelectTrigger data-testid="report-range-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7d">Last 7 days</SelectItem>
                        <SelectItem value="30d">Last 30 days</SelectItem>
                        <SelectItem value="90d">Last 90 days</SelectItem>
                        <SelectItem value="180d">Last 180 days</SelectItem>
                        <SelectItem value="360d">Last 360 days</SelectItem>
                        <SelectItem value="720d">Last 720 days</SelectItem>
                        <SelectItem value="1080d">Last 1080 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Report recipient</Label>
                  <Input type="email" value={s.report_recipient} onChange={(e) => set("report_recipient", e.target.value)} placeholder="exec@company.com" data-testid="report-recipient-input" />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Personal</p>
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  Email me a weekly summary
                  <p className="text-[11px] text-muted-foreground">Sent to {s.integrations ? "your account email" : ""} every Monday.</p>
                </div>
                <Switch checked={weekly} onCheckedChange={toggleWeekly} data-testid="toggle-weekly-summary-switch" />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={sendNow} data-testid="send-report-now-button"><Send className="h-4 w-4 mr-1.5" />Send report now</Button>
              <Button onClick={save} disabled={saving} data-testid="save-settings-button">{saving ? "Saving…" : "Save settings"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------------- New Dashboard ----------------
export function NewDashboardDialog({ datasets, onCreate }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [datasetId, setDatasetId] = useState("builtin");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("Give your dashboard a name");
    setSaving(true);
    try {
      await onCreate(name.trim(), datasetId);
      setOpen(false);
      setName("");
      setDatasetId("builtin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create dashboard");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="new-dashboard-button"><Plus className="h-4 w-4 mr-1" />New</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-dashboard-dialog">
        <DialogHeader><DialogTitle className="font-heading">New Dashboard</DialogTitle><DialogDescription>Create a new dashboard, optionally from a data source.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Dashboard name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales Overview" data-testid="new-dashboard-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Data source</Label>
            <Select value={datasetId} onValueChange={setDatasetId}>
              <SelectTrigger data-testid="new-dashboard-dataset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {d.metric_count} metrics</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Add your own data sources from the “Data” button.</p>
          </div>
        </div>
        <DialogFooter><Button onClick={create} disabled={saving} data-testid="confirm-new-dashboard-button">{saving ? "Creating…" : "Create dashboard"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormatSelect({ value, onChange, className = "w-24 h-9" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="number">Number</SelectItem>
        <SelectItem value="currency">Currency</SelectItem>
        <SelectItem value="percent">Percent</SelectItem>
      </SelectContent>
    </Select>
  );
}

function UrlSourceForm({ urlType, setUrlType, name, setName, url, setUrl, jsonPath, setJsonPath,
                         authHeader, setAuthHeader, authValue, setAuthValue, interval, setIntervalVal,
                         busy, onBack, onNext }) {
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" variant={urlType === "file" ? "default" : "outline"} onClick={() => setUrlType("file")} data-testid="urltype-file-button">CSV / Google Sheet</Button>
        <Button size="sm" variant={urlType === "json" ? "default" : "outline"} onClick={() => setUrlType("json")} data-testid="urltype-json-button">JSON API</Button>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Data source name (optional)</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Live Sales" data-testid="url-name-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{urlType === "json" ? "API endpoint URL" : "Data URL"}</Label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={urlType === "json" ? "https://api.example.com/metrics" : "Public CSV URL or Google Sheet link"} data-testid="url-input" />
        <p className="text-[11px] text-muted-foreground">
          {urlType === "json"
            ? "Endpoint must return a JSON array of objects (or an object containing one). Each object = one row."
            : "Paste a public CSV/XLSX URL, or a Google Sheet link (Publish to web, or link with view access). We auto-convert Sheet links to CSV."}
        </p>
      </div>
      {urlType === "json" && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Data path (optional)</Label>
            <Input value={jsonPath} onChange={(e) => setJsonPath(e.target.value)} placeholder="e.g. data.results (leave blank if the array is at the top level)" data-testid="json-path-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Auth header (optional)</Label>
            <div className="flex gap-2">
              <Input className="w-40" value={authHeader} onChange={(e) => setAuthHeader(e.target.value)} placeholder="Authorization" data-testid="auth-header-input" />
              <Input className="flex-1" value={authValue} onChange={(e) => setAuthValue(e.target.value)} placeholder="Bearer <token>" data-testid="auth-value-input" />
            </div>
          </div>
        </>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Auto-refresh</Label>
        <Select value={interval} onValueChange={setIntervalVal}>
          <SelectTrigger className="h-9" data-testid="url-interval-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hourly">Every hour</SelectItem>
            <SelectItem value="6h">Every 6 hours</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button onClick={onNext} disabled={busy} data-testid="url-preview-button">{busy ? "Fetching…" : "Next: map columns"}</Button>
      </DialogFooter>
    </div>
  );
}

function ColumnMappingForm({ name, setName, dateCol, setDateCol, cols, setColField, preview,
                            makeDash, setMakeDash, mapSource, busy, onBack, onConfirm }) {
  return (
    <div className="space-y-3" data-testid="column-mapping">
      <div className="space-y-1">
        <Label className="text-xs">Data source name (optional)</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Defaults to file name" data-testid="map-name-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Which column is the date?</Label>
        <Select value={String(dateCol)} onValueChange={(v) => setDateCol(parseInt(v, 10))}>
          <SelectTrigger className="h-9" data-testid="date-col-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {preview.columns.map((c, i) => (
              <SelectItem key={`${c}-${i}`} value={String(i)}>{c || `Column ${i + 1}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Metrics ({preview.row_count} rows)</Label>
        <div className="space-y-2 max-h-56 overflow-y-auto mt-1">
          {cols.filter((c) => c.index !== dateCol).map((c) => (
            <div key={c.index} className="flex gap-2 items-center" data-testid={`map-col-row-${c.index}`}>
              <Checkbox checked={c.include} onCheckedChange={(v) => setColField(c.index, "include", !!v)} data-testid={`map-col-include-${c.index}`} />
              <Input className="flex-1 h-8" value={c.label} onChange={(e) => setColField(c.index, "label", e.target.value)} placeholder={`Column ${c.index + 1}`} data-testid={`map-col-label-${c.index}`} />
              <FormatSelect value={c.format} onChange={(v) => setColField(c.index, "format", v)} className="w-24 h-8" />
            </div>
          ))}
        </div>
      </div>
      {preview.sample?.length > 0 && (
        <div className="text-[11px] text-muted-foreground overflow-x-auto border border-border rounded-md p-2">
          <div className="font-medium mb-1">Preview</div>
          <table className="w-full">
            <tbody>
              {preview.sample.slice(0, 3).map((r, ri) => (
                <tr key={`prow-${ri}`}>{r.map((cell, ci) => (<td key={`pcell-${ri}-${ci}`} className={`pr-3 py-0.5 font-mono ${ci === dateCol ? "text-primary" : ""}`}>{cell}</td>))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <Checkbox id="make-dash" checked={makeDash} onCheckedChange={(v) => setMakeDash(!!v)} data-testid="make-dashboard-checkbox" />
        <Label htmlFor="make-dash" className="text-xs cursor-pointer">Also create a dashboard from this data source</Label>
      </div>
      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button onClick={onConfirm} disabled={busy} data-testid="confirm-import-button">{busy ? "Importing…" : (mapSource === "file" ? "Import file" : "Connect")}</Button>
      </DialogFooter>
    </div>
  );
}


// ---------------- Data Sources ----------------
export function DataSourceDialog({ datasets, onChange, onCreateDashboard, open: openProp, onOpenChange: onOpenChangeProp, startMode }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChangeProp || setOpenInternal;
  const [mode, setMode] = useState("list"); // list | manual | csv | map
  const [name, setName] = useState("");
  const [metrics, setMetrics] = useState([{ id: "m0", label: "", values: "", format: "number", unit: "" }]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { columns, sample, row_count }
  const [dateCol, setDateCol] = useState(0);
  const [cols, setCols] = useState([]); // [{ index, label, format, include }]
  const [makeDash, setMakeDash] = useState(true);
  const [url, setUrl] = useState("");
  const [interval, setIntervalVal] = useState("daily");
  const [mapSource, setMapSource] = useState("file"); // file | url | gsheet
  const [refreshing, setRefreshing] = useState(null);
  const [urlType, setUrlType] = useState("file"); // file (csv/sheet) | json
  const [jsonPath, setJsonPath] = useState("");
  const [authHeader, setAuthHeader] = useState("");
  const [authValue, setAuthValue] = useState("");
  const [gs, setGs] = useState({ configured: false, connected: false });
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetRange, setSheetRange] = useState("");

  const reset = () => { setMode("list"); setName(""); setMetrics([{ id: "m0", label: "", values: "", format: "number", unit: "" }]); setFile(null); setPreview(null); setDateCol(0); setCols([]); setMakeDash(true); setUrl(""); setIntervalVal("daily"); setMapSource("file"); setUrlType("file"); setJsonPath(""); setAuthHeader(""); setAuthValue(""); setSheetUrl(""); setSheetRange(""); };

  useEffect(() => { if (open && startMode) setMode(startMode); }, [open, startMode]);
  useEffect(() => { if (open) api.get("/oauth/sheets/status").then((r) => setGs(r.data)).catch(() => {}); }, [open]);

  const connectGoogle = async () => {
    try { const r = await api.get("/oauth/sheets/login"); window.location.href = r.data.auth_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start Google connection"); }
  };
  const onGoogleClick = () => {
    if (!gs.configured) return toast.error("Google Sheets isn't configured on the server yet");
    if (!gs.connected) return connectGoogle();
    setMode("gsheet");
  };
  const loadGsheetPreview = async () => {
    if (!sheetUrl.trim()) return toast.error("Paste a Google Sheet link");
    setBusy(true);
    try {
      const r = await api.post("/datasets/gsheet/preview", { url: sheetUrl.trim(), sheet_range: sheetRange.trim() || undefined });
      setMapSource("gsheet");
      applyPreviewData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the sheet"); }
    finally { setBusy(false); }
  };

  const addRow = () => setMetrics([...metrics, { id: `m${Date.now()}`, label: "", values: "", format: "number", unit: "" }]);
  const setRow = (i, k, v) => setMetrics(metrics.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const rmRow = (i) => setMetrics(metrics.filter((_, idx) => idx !== i));

  const createManual = async () => {
    if (!name.trim()) return toast.error("Name your data source");
    const parsed = metrics
      .filter((m) => m.label.trim() && m.values.trim())
      .map((m) => ({
        label: m.label.trim(), format: m.format, unit: m.unit,
        values: m.values.split(",").map((x) => parseFloat(x.trim())).filter((n) => !isNaN(n)),
      }))
      .filter((m) => m.values.length);
    if (!parsed.length) return toast.error("Add at least one metric with comma-separated values");
    setBusy(true);
    try {
      await api.post("/datasets", { name: name.trim(), metrics: parsed });
      toast.success("Data source created");
      await onChange();
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  const applyPreviewData = (data) => {
    const columns = data.columns || [];
    const suggested = data.suggested_date_col ?? 0;
    const fmts = data.suggested_formats || [];
    const numeric = data.numeric || [];
    setPreview(data);
    setDateCol(suggested);
    setCols(columns.map((c, i) => ({ index: i, label: c || `Column ${i + 1}`, format: fmts[i] || "number", include: i !== suggested && numeric[i] !== false })));
    setMode("map");
  };

  const loadPreview = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file");
    const fn = (file.name || "").toLowerCase();
    if (!fn.endsWith(".csv") && !fn.endsWith(".xlsx")) return toast.error("Only .csv or .xlsx files are supported");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/datasets/preview", fd);
      setMapSource("file");
      applyPreviewData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the file");
    } finally { setBusy(false); }
  };

  const urlPayload = () => {
    const p = { url: url.trim() };
    if (urlType === "json") {
      if (jsonPath.trim()) p.json_path = jsonPath.trim();
      if (authHeader.trim() && authValue.trim()) p.headers = { [authHeader.trim()]: authValue.trim() };
    }
    return p;
  };

  const loadUrlPreview = async () => {
    if (!url.trim()) return toast.error("Paste a data URL");
    setBusy(true);
    try {
      const r = await api.post("/datasets/url/preview", urlPayload());
      setMapSource("url");
      applyPreviewData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch the URL");
    } finally { setBusy(false); }
  };

  const setColField = (i, k, v) => setCols(cols.map((c) => c.index === i ? { ...c, [k]: v } : c));

  const importMapped = async () => {
    const chosen = cols.filter((c) => c.index !== dateCol && c.include);
    if (!chosen.length) return toast.error("Include at least one metric column");
    const mapping = chosen.map((c) => ({ index: c.index, label: c.label, format: c.format, include: true }));
    setBusy(true);
    try {
      let res;
      if (mapSource === "url") {
        res = await api.post("/datasets/url", { ...urlPayload(), name: name.trim(), interval, date_col: dateCol, mapping });
        toast.success("Live data source connected");
      } else if (mapSource === "gsheet") {
        res = await api.post("/datasets/gsheet", { name: name.trim(), url: sheetUrl.trim(), sheet_range: sheetRange.trim() || undefined, interval, date_col: dateCol, mapping });
        toast.success("Google Sheet connected");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        if (name.trim()) fd.append("name", name.trim());
        fd.append("date_col", String(dateCol));
        fd.append("mapping", JSON.stringify(mapping));
        res = await api.post("/datasets/upload", fd);
        toast.success("File imported");
      }
      await onChange();
      if (makeDash && onCreateDashboard) {
        const dsName = res.data?.name || "Imported data";
        await onCreateDashboard(dsName, res.data.id);
        setOpen(false);
      }
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to import");
    } finally { setBusy(false); }
  };

  const refreshSource = async (id) => {
    setRefreshing(id);
    try { const r = await api.post(`/datasets/${id}/refresh`); toast.success("Data refreshed"); await onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/datasets/${id}`); toast.success("Data source deleted"); await onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const custom = datasets.filter((d) => d.id !== "builtin");

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-data-button"><Database className="h-4 w-4 mr-1.5" />Data</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="data-dialog">
        <DialogHeader><DialogTitle className="font-heading">Data Sources</DialogTitle><DialogDescription>Add data via manual entry, file upload, or a live URL/API connection.</DialogDescription></DialogHeader>

        {mode === "list" && (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => setMode("manual")} data-testid="new-manual-dataset-button"><Plus className="h-4 w-4 mr-1" />Manual entry</Button>
              <Button size="sm" variant="outline" onClick={() => setMode("csv")} data-testid="new-csv-dataset-button"><Upload className="h-4 w-4 mr-1.5" />Upload CSV / Excel</Button>
              <Button size="sm" variant="outline" onClick={() => setMode("url")} data-testid="new-url-dataset-button"><Link2 className="h-4 w-4 mr-1.5" />Connect URL</Button>
              <Button size="sm" variant="outline" onClick={onGoogleClick} data-testid="new-gsheet-dataset-button" title={!gs.configured ? "Requires server-side Google OAuth setup" : (gs.connected ? "Connected" : "Connect your Google account")}><FileSpreadsheet className="h-4 w-4 mr-1.5" />Google Sheets{gs.connected ? " ✓" : ""}</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between border border-border rounded-md p-2.5 opacity-70">
                <span className="text-sm">Sample KPIs (built-in)</span>
                <Badge variant="secondary" className="text-[10px]">default</Badge>
              </div>
              {custom.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`dataset-row-${d.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      {d.source === "url" && <span className={`h-2 w-2 rounded-full shrink-0 ${d.last_status === "error" ? "bg-destructive" : "bg-[hsl(142_71%_45%)]"}`} title={d.last_status === "error" ? (d.last_error || "Last refresh failed") : "Connection healthy"} data-testid={`health-dot-${d.id}`} />}
                      {d.name}
                      {d.source === "url" && <Badge variant="secondary" className="text-[9px] uppercase">{d.kind === "api" ? "API" : "live"} · {d.interval}</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{d.source} · {d.metric_count} metrics{d.last_refreshed ? ` · updated ${new Date(d.last_refreshed).toLocaleString()}` : ""}</div>
                    {d.source === "url" && d.last_status === "error" && <div className="text-[11px] text-destructive truncate" data-testid={`health-error-${d.id}`}>⚠ {d.last_error || "Last refresh failed"}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {d.source === "url" && (
                      <button onClick={() => refreshSource(d.id)} disabled={refreshing === d.id} className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50" data-testid={`refresh-dataset-${d.id}`} title="Refresh now"><RefreshCw className={`h-4 w-4 ${refreshing === d.id ? "animate-spin" : ""}`} /></button>
                    )}
                    <button onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-dataset-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {custom.length === 0 && <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">No custom data sources yet</p>}
            </div>
          </div>
        )}

        {mode === "manual" && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Data source name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q2 Sales" data-testid="manual-name-input" />
            </div>
            <Label className="text-xs">Metrics (comma-separated values, oldest → newest)</Label>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {metrics.map((m, i) => (
                <div key={m.id} className="flex gap-2 items-start" data-testid={`manual-metric-row-${i}`}>
                  <Input className="w-32" value={m.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Metric" data-testid={`manual-metric-label-${i}`} />
                  <Input className="flex-1" value={m.values} onChange={(e) => setRow(i, "values", e.target.value)} placeholder="10, 12, 15, 14" data-testid={`manual-metric-values-${i}`} />
                  <Select value={m.format} onValueChange={(v) => setRow(i, "format", v)}>
                    <SelectTrigger className="w-24 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="currency">Currency</SelectItem>
                      <SelectItem value="percent">Percent</SelectItem>
                    </SelectContent>
                  </Select>
                  {metrics.length > 1 && <button onClick={() => rmRow(i)} className="mt-2 text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addRow} data-testid="add-manual-metric-button"><Plus className="h-4 w-4 mr-1" />Add metric</Button>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={reset}>Back</Button>
              <Button onClick={createManual} disabled={busy} data-testid="save-manual-dataset-button">{busy ? "Saving…" : "Create data source"}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "csv" && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Data source name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Defaults to file name" data-testid="csv-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">CSV or Excel file</Label>
              <Input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
              <p className="text-[11px] text-muted-foreground">Supported: .csv and .xlsx (max 2MB). Format: first column = date, remaining columns = metrics. First row = header. Excel uses the first sheet.</p>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-muted-foreground">Need a starting point?</span>
              <a href={`${API}/datasets/template?format=csv`} className="text-primary hover:underline" data-testid="download-csv-template">Download CSV template</a>
              <span className="text-muted-foreground">·</span>
              <a href={`${API}/datasets/template?format=xlsx`} className="text-primary hover:underline" data-testid="download-xlsx-template">Excel template</a>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={reset}>Back</Button>
              <Button onClick={loadPreview} disabled={busy} data-testid="upload-csv-button">{busy ? "Reading…" : "Next: map columns"}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "gsheet" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-muted-foreground">Reading private sheets via your connected Google account.</p>
              <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={async () => { try { await api.post("/oauth/sheets/disconnect"); setGs({ ...gs, connected: false }); setMode("list"); toast.success("Google disconnected"); } catch (e) {} }} data-testid="gsheet-disconnect-button">Disconnect</Button>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data source name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q3 Forecast" data-testid="gsheet-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Google Sheet link</Label>
              <Input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/.../edit" data-testid="gsheet-url-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Range / tab (optional)</Label>
              <Input value={sheetRange} onChange={(e) => setSheetRange(e.target.value)} placeholder="e.g. Sheet1 or Sheet1!A1:E500 (blank = first tab)" data-testid="gsheet-range-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Auto-refresh</Label>
              <Select value={interval} onValueChange={setIntervalVal}>
                <SelectTrigger className="h-9" data-testid="gsheet-interval-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Every hour</SelectItem>
                  <SelectItem value="6h">Every 6 hours</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={reset}>Back</Button>
              <Button onClick={loadGsheetPreview} disabled={busy} data-testid="gsheet-preview-button">{busy ? "Reading…" : "Next: map columns"}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "url" && (
          <UrlSourceForm
            urlType={urlType} setUrlType={setUrlType}
            name={name} setName={setName}
            url={url} setUrl={setUrl}
            jsonPath={jsonPath} setJsonPath={setJsonPath}
            authHeader={authHeader} setAuthHeader={setAuthHeader}
            authValue={authValue} setAuthValue={setAuthValue}
            interval={interval} setIntervalVal={setIntervalVal}
            busy={busy} onBack={reset} onNext={loadUrlPreview}
          />
        )}

        {mode === "map" && preview && (
          <ColumnMappingForm
            name={name} setName={setName}
            dateCol={dateCol} setDateCol={setDateCol}
            cols={cols} setColField={setColField}
            preview={preview} makeDash={makeDash} setMakeDash={setMakeDash}
            mapSource={mapSource} busy={busy}
            onBack={() => setMode(mapSource === "url" ? "url" : mapSource === "gsheet" ? "gsheet" : "csv")}
            onConfirm={importMapped}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------------- Rename Dashboard ----------------
export function RenameDashboardDialog({ dashId, currentName, onRenamed }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentName || "");
  useEffect(() => { setName(currentName || ""); }, [currentName, open]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name can't be empty");
    try {
      await api.put(`/dashboards/${dashId}`, { name: name.trim() });
      toast.success("Dashboard renamed");
      setOpen(false);
      onRenamed && onRenamed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="rename-dashboard-button" title="Rename dashboard"><Pencil className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="rename-dashboard-dialog">
        <DialogHeader><DialogTitle className="font-heading">Rename Dashboard</DialogTitle><DialogDescription>Give this dashboard a new name.</DialogDescription></DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Dashboard name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="rename-dashboard-input" onKeyDown={(e) => e.key === "Enter" && save()} />
        </div>
        <DialogFooter><Button onClick={save} data-testid="confirm-rename-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Per-dashboard read-only share ----------------
export function DashShareDialog({ dashId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/dashboards/${dashId}/shares`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, dashId]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/dashboards/${dashId}/share`);
      toast.success("Read-only link created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const copy = (token, id) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(id);
    toast.success("Link copied");
    setTimeout(() => setCopied(""), 1500);
  };

  const remove = async (id) => { await api.delete(`/dashboards/${dashId}/shares/${id}`); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="dash-share-button" title="Share read-only link"><Link2 className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="dash-share-dialog">
        <DialogHeader><DialogTitle className="font-heading">Share this dashboard</DialogTitle><DialogDescription>Generate a public read-only link to this dashboard.</DialogDescription></DialogHeader>
        <p className="text-sm text-muted-foreground">Anyone with the link can view this dashboard read-only — no sign-in required.</p>
        <Button onClick={create} disabled={busy} data-testid="create-dash-share-button"><Plus className="h-4 w-4 mr-1" />{busy ? "Creating…" : "Create read-only link"}</Button>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">No links yet</p>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`dash-share-row-${r.id}`}>
              <div className="text-xs text-muted-foreground font-mono truncate mr-2">/share/{r.token}</div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => copy(r.token, r.id)} className="h-7 w-7 flex items-center justify-center rounded border border-border hover:bg-secondary transition-colors" data-testid={`copy-dash-share-${r.id}`}>
                  {copied === r.id ? <Check className="h-3.5 w-3.5 text-[hsl(142_71%_45%)]" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => remove(r.id)} className="h-7 w-7 flex items-center justify-center rounded border border-border hover:text-destructive transition-colors" data-testid={`delete-dash-share-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Send Report (on demand + schedule) ----------------
export function SendReportDialog({ dashId, range = "30d" }) {
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [freq, setFreq] = useState("daily");
  const [schedules, setSchedules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [formats, setFormats] = useState(["summary_pdf", "timeseries_xlsx"]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const FORMAT_OPTS = [
    { id: "summary_pdf", label: "Summary PDF" },
    { id: "summary_csv", label: "Summary CSV" },
    { id: "timeseries_csv", label: "Full data (CSV)" },
    { id: "timeseries_xlsx", label: "Full data (Excel)" },
  ];
  const toggleFormat = (f) => setFormats((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);

  const loadSchedules = () => api.get(`/dashboards/${dashId}/schedules`).then((r) => setSchedules(r.data)).catch(() => setSchedules([]));
  const loadLogs = () => api.get(`/dashboards/${dashId}/report-logs`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  useEffect(() => { if (open && dashId) { loadSchedules(); loadLogs(); } /* eslint-disable-next-line */ }, [open, dashId]);

  const recipientsArr = () => emails.split(",").map((e) => e.trim()).filter(Boolean);

  const send = async () => {
    const recipients = recipientsArr();
    if (!recipients.length) return toast.error("Enter at least one email address");
    if (!formats.length) return toast.error("Pick at least one attachment format");
    setBusy(true);
    try {
      const { data } = await api.post(`/dashboards/${dashId}/send-report`, { recipients, range, formats, subject, message, reply_to: replyTo });
      if (data.error) toast.error(`Email not sent: ${data.error}`);
      else if (data.mocked) toast.warning(`Report generated for ${data.sent} recipient(s) — email is MOCKED (add RESEND_API_KEY to send for real)`);
      else toast.success(`Report sent to ${data.sent} recipient(s)`);
      if (!data.error) { setOpen(false); setEmails(""); }
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send report");
    } finally { setBusy(false); }
  };

  const schedule = async () => {
    const recipients = recipientsArr();
    if (!recipients.length) return toast.error("Enter recipient emails above first");
    if (!formats.length) return toast.error("Pick at least one attachment format");
    try {
      await api.post(`/dashboards/${dashId}/schedules`, { recipients, frequency: freq, range, formats, subject, message, reply_to: replyTo });
      toast.success(`Scheduled ${freq} delivery`);
      setEmails("");
      loadSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule");
    }
  };

  const removeSchedule = async (id) => { await api.delete(`/dashboards/${dashId}/schedules/${id}`); loadSchedules(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-send-report-button"><Send className="h-4 w-4 mr-1.5" />Send Report</Button>
      </DialogTrigger>
      <DialogContent data-testid="send-report-dialog">
        <DialogHeader><DialogTitle className="font-heading">Email this dashboard</DialogTitle><DialogDescription>Send a report snapshot to one or more recipients.</DialogDescription></DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Recipient emails (comma-separated)</Label>
          <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ceo@company.com, ops@company.com" data-testid="report-emails-input" onKeyDown={(e) => e.key === "Enter" && send()} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Subject line <span className="text-muted-foreground normal-case">(optional)</span></Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Your monthly KPI report" data-testid="report-subject-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Reply-to email <span className="text-muted-foreground normal-case">(optional)</span></Label>
          <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="you@leanconsultingts.com" data-testid="report-replyto-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Intro message <span className="text-muted-foreground normal-case">(optional)</span></Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi team, here's this month's performance snapshot…" rows={2} data-testid="report-message-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Attachments</Label>
          <div className="grid grid-cols-2 gap-2">
            {FORMAT_OPTS.map((f) => (
              <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid={`format-option-${f.id}`}>
                <Checkbox checked={formats.includes(f.id)} onCheckedChange={() => toggleFormat(f.id)} data-testid={`format-checkbox-${f.id}`} />
                {f.label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Excel &amp; full-data files include a summary tab plus one tab per metric.</p>
        </div>
        <Button onClick={send} disabled={busy} data-testid="send-report-submit-button"><Send className="h-4 w-4 mr-1.5" />{busy ? "Sending…" : "Send now"}</Button>

        <div className="border-t border-border pt-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Automate delivery</p>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Frequency</Label>
              <Select value={freq} onValueChange={setFreq}>
                <SelectTrigger data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily (08:00 UTC)</SelectItem>
                  <SelectItem value="weekly">Weekly (Mon 08:00 UTC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={schedule} data-testid="create-schedule-button"><Plus className="h-4 w-4 mr-1" />Schedule</Button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {schedules.length === 0 && <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-border rounded-md">No scheduled reports</p>}
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`schedule-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium capitalize">{s.frequency} · {s.range}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">{s.recipients.join(", ")}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{(s.formats || []).length} attachment(s)</div>
                </div>
                <button onClick={() => removeSchedule(s.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0 ml-2" data-testid={`delete-schedule-${s.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Delivery history</p>
          <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="report-logs-list">
            {logs.length === 0 && <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-border rounded-md">No reports sent yet</p>}
            {logs.map((l) => (
              <div key={l.id} className="border border-border rounded-md p-2.5" data-testid={`report-log-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{l.subject}</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-border text-muted-foreground shrink-0">{l.trigger}</span>
                </div>
                <div className="text-[11px] text-muted-foreground font-mono mb-1">{new Date(l.created_at).toLocaleString()}</div>
                <div className="flex flex-col gap-0.5">
                  {l.recipients.map((rc, i) => {
                    const c = rc.status === "delivered" ? "bg-[hsl(142_71%_45%)]"
                      : rc.status === "sent" ? "bg-[hsl(217_91%_60%)]"
                      : rc.status === "mocked" ? "bg-muted-foreground"
                      : "bg-destructive";
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`report-log-recipient-${l.id}-${i}`} title={rc.error || rc.status}>
                        <span className={`h-2 w-2 rounded-full shrink-0 ${c}`} />
                        <span className="font-mono truncate">{rc.email}</span>
                        <span className="text-muted-foreground uppercase text-[10px] tracking-wider ml-auto shrink-0">{rc.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------------- Team workspace (invite members + share dashboard with team) ----------------
export function TeamDialog({ dashId, shared, onShareChange }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberOf, setMemberOf] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);

  const load = () => api.get("/workspace/me").then((r) => {
    setMembers(r.data.members || []);
    setMemberOf(r.data.member_of || []);
  }).catch((e) => { console.error("Failed to load workspace", e); });
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const invite = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    try {
      await api.post("/workspace/invite", { email: e });
      setEmail("");
      toast.success("Teammate invited");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/workspace/members/${id}`);
    load();
    toast.success("Teammate removed");
  };

  const toggleShare = async () => {
    setSharing(true);
    try {
      const r = await api.post(`/dashboards/${dashId}/team-share`, { shared: !shared });
      toast.success(r.data.shared ? "Dashboard shared with your team" : "Removed from team");
      onShareChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update sharing");
    } finally { setSharing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-team-button"><Users className="h-4 w-4 mr-1.5" />Team</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Team workspace</DialogTitle>
          <DialogDescription>Invite teammates and share dashboards with your team.</DialogDescription>
        </DialogHeader>

        {dashId && (
          <div className="flex items-center justify-between border border-border rounded-md p-3" data-testid="team-share-row">
            <div className="mr-3">
              <div className="text-sm font-medium">Share the current dashboard with your team</div>
              <div className="text-xs text-muted-foreground">Teammates can view it read-only in their own account.</div>
            </div>
            <Switch checked={!!shared} onCheckedChange={toggleShare} disabled={sharing} data-testid="team-share-switch" />
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invite a teammate</Label>
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" onKeyDown={(e) => e.key === "Enter" && invite()} data-testid="team-invite-input" />
            <Button onClick={invite} disabled={busy} data-testid="team-invite-button"><Plus className="h-4 w-4 mr-1" />Invite</Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Teammates need their own active subscription to open shared dashboards.</p>
        </div>

        <div className="space-y-2 max-h-56 overflow-y-auto">
          {members.length === 0 && <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">No teammates yet</p>}
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`team-member-${m.email}`}>
              <div className="min-w-0 mr-2">
                <div className="text-sm font-medium font-mono truncate">{m.email}</div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{m.status === "pending" ? "Invited · not registered yet" : "Active"}</div>
              </div>
              <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0" data-testid={`remove-team-member-${m.email}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>

        {memberOf.length > 0 && (
          <div className="space-y-1.5 border-t border-border pt-3">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Teams you belong to</Label>
            {memberOf.map((w) => (
              <div key={w.id} className="text-sm text-muted-foreground" data-testid={`member-of-${w.id}`}>{w.name} · <span className="font-mono">{w.owner_email}</span></div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
